import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/api";
import { signOut } from "@/lib/clerk";
import { HOME_URL } from "@/lib/home";

/**
 * Copy for each way the gate can refuse. The `reason` comes from the API's
 * error body; a 500 carries none, and must not read as "your session ended".
 */
const DENIAL: Record<string, { title: string; body: string }> = {
  unauthenticated: {
    title: "Sign in required",
    body: "Your session has ended. Sign in with your Ley Choon email to continue.",
  },
  deactivated: {
    title: "Access suspended",
    body: "Your access to Solution Brief has been suspended. Contact HR if you think this is a mistake.",
  },
  forbidden_domain: {
    title: "Ley Choon accounts only",
    body: "Solution Brief is for @leychoon.com accounts. Sign in with your work email.",
  },
  not_registered: {
    title: "Not registered",
    body: "Your account exists but has no role here yet. Contact HR.",
  },
  no_email: {
    title: "Sign-in misconfigured",
    body: "Your sign-in token carried no email address, so we cannot identify you. Please report this to IT.",
  },
};

export function denialFor(error: unknown): { title: string; body: string } {
  const reason =
    error instanceof ApiError
      ? (error.reason ?? (error.status === 401 ? "unauthenticated" : null))
      : null;
  return (
    (reason && DENIAL[reason]) ?? {
      title: "Something went wrong",
      body: "We could not confirm your access. Try again in a moment.",
    }
  );
}

/**
 * A refusal screen for someone who still holds a Clerk session. ⚠ It mounts
 * nothing of Clerk's and never signs out on its own: the session is shared by
 * every leychoon.app app, and the next move is the person's.
 */
export function Refused({ error }: { error: unknown }) {
  const copy = denialFor(error);
  return (
    <main className="mx-auto grid min-h-dvh max-w-md place-items-center px-6 py-10">
      <div className="w-full text-center">
        <img src="/logo.svg" alt="" width={48} height={48} className="mx-auto mb-4 rounded-xl" />
        <h1 className="text-lg font-semibold">{copy.title}</h1>
        <p className="text-muted-foreground mt-2 text-sm">{copy.body}</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button onClick={() => window.location.reload()}>Try again</Button>
          <Button variant="outline" onClick={() => window.location.assign(HOME_URL)}>
            Back to Home
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              void signOut().finally(() => window.location.reload());
            }}
          >
            Sign out
          </Button>
        </div>
      </div>
    </main>
  );
}
